import { useCallback, useEffect, useRef } from 'react';

import { createBuiltinColumnVariableKeys, createColumnVariableKeys } from '../utils';

import type { MouseEvent, RefObject, TouchEvent } from 'react';
import type { Header, RowData, Table } from '@tanstack/react-table';

interface UseColumnResizeOptions<TData extends RowData> {
  hasAutoPadColumn: boolean;
  hasExpandColumn: boolean;
  hasSelectionColumn: boolean;
  table: Table<TData>;
  tableContainerRef: RefObject<HTMLDivElement | null>;
  tableContainerWidth: number | null;
  tableRootRef: RefObject<HTMLDivElement | null>;
  showSideMargin?: boolean;
}

/**
 * Find the last unfixed (not pinned) header in a header group
 */
function findLastUnfixedHeader<TData extends RowData>(
  headers: Header<TData, unknown>[],
): { header: Header<TData, unknown>; index: number } | null {
  const lastIndex = headers.findLastIndex(h =>
    h.getLeafHeaders().some(leaf => leaf.column.getIsPinned() === false),
  );

  if (lastIndex === -1) return null;

  return {
    header: headers[lastIndex],
    index: lastIndex,
  };
}

/**
 * Calculate total width of all headers including margins
 */
function calculateTableTotalWidth<TData extends RowData>(params: {
  computedStyle: CSSStyleDeclaration;
  hasExpandColumn: boolean;
  hasSelectionColumn: boolean;
  headers: Header<TData, unknown>[];
  showSideMargin?: boolean;
}): number {
  const { computedStyle, hasExpandColumn, hasSelectionColumn, headers, showSideMargin } = params;

  let totalWidth =
    (showSideMargin ? /* 양쪽 마진 */ 40 : 0) +
    (hasSelectionColumn ? /* 체크박스 컬럼 */ 48 : 0) +
    (hasExpandColumn ? /* 펼치가 컬럼 */ 48 : 0);

  for (const h of headers) {
    const hVariableKey = createColumnVariableKeys(h.column.id);
    const sizeStr = computedStyle.getPropertyValue(hVariableKey.size);
    const size = parseFloat(sizeStr) || h.getSize();
    totalWidth += size;
  }

  return totalWidth;
}

/**
 * Apply auto-sizing logic to the last unfixed column
 */
function applyLastColumnAutoSizing<TData extends RowData>(params: {
  computedStyle: CSSStyleDeclaration;
  lastUnfixedHeader: Header<TData, unknown>;
  tableContainerWidth: number | null;
  tableRootRef: HTMLDivElement;
  totalWidth: number;
}): void {
  const { computedStyle, lastUnfixedHeader, tableContainerWidth, tableRootRef, totalWidth } =
    params;

  const lastHeaderVariableKey = createColumnVariableKeys(lastUnfixedHeader.column.id);

  if (tableContainerWidth && tableContainerWidth > totalWidth) {
    // Table has empty space → set last column to auto
    tableRootRef.style.setProperty(lastHeaderVariableKey.size, 'auto');
  } else {
    // No empty space → reset to fixed size if it was auto
    const currentLastSize = computedStyle.getPropertyValue(lastHeaderVariableKey.size);
    if (currentLastSize === 'auto') {
      const size = lastUnfixedHeader.getSize();
      tableRootRef.style.setProperty(lastHeaderVariableKey.size, `${size}px`);
    }
  }
}

/**
 * Handle auto-scroll when resizing last column to the right
 */
function handleAutoScroll(params: {
  deltaX: number;
  headerId: string;
  isResizingLastColumn: boolean;
  prevDeltaX: number;
  tableContainerRef: HTMLDivElement | null;
  tableContainerWidth: number | null;
  totalWidth: number;
}): void {
  const {
    deltaX,
    isResizingLastColumn,
    prevDeltaX,
    tableContainerRef,
    tableContainerWidth,
    totalWidth,
  } = params;

  if (
    isResizingLastColumn &&
    deltaX > 0 && // Expanding to the right
    tableContainerWidth &&
    totalWidth > tableContainerWidth &&
    tableContainerRef
  ) {
    // Scroll by the delta since last frame
    const scrollDelta = deltaX - prevDeltaX;
    tableContainerRef.scrollLeft += scrollDelta;
  }
}

/**
 * Custom hook for column resizing with CSS variable manipulation
 */
export default function useColumnResize<TData extends RowData>(
  options: UseColumnResizeOptions<TData>,
) {
  const {
    hasAutoPadColumn,
    hasExpandColumn,
    hasSelectionColumn,
    showSideMargin,
    table,
    tableContainerRef,
    tableContainerWidth,
    tableRootRef,
  } = options;

  const cleanupRef = useRef<(() => void) | null>(null);

  // Cleanup event listeners on unmount
  useEffect(() => {
    return () => {
      cleanupRef.current?.();
    };
  }, []);

  const handleColumnResizeEvent = useCallback(
    (
      event: MouseEvent<HTMLDivElement> | TouchEvent<HTMLDivElement>,
      headerId: string,
      cellElement: HTMLTableCellElement,
    ) => {
      if (
        !(
          event.nativeEvent instanceof globalThis.MouseEvent ||
          event.nativeEvent instanceof globalThis.TouchEvent
        )
      )
        return;
      if (!tableRootRef.current) return;

      // Prevent default and stop propagation
      event.nativeEvent.preventDefault();
      event.nativeEvent.stopPropagation();

      // Cache header groups (calculate once, use multiple times)
      const headerGroups = table.getHeaderGroups();
      const headerGroup = headerGroups[0]; // Usually only one header group
      if (!headerGroup) return;

      // Find header and column info
      const header = headerGroup.headers.find(h => h.id === headerId);
      if (!header) return;

      const { minSize = 60, maxSize = Number.MAX_SAFE_INTEGER } = header.column.columnDef;
      const columnVariableKey = createColumnVariableKeys(header.column.id);

      // Get initial size from current CSS variable (call once)
      const initialComputedStyle = getComputedStyle(tableRootRef.current);
      const currentSizeStr = initialComputedStyle.getPropertyValue(columnVariableKey.size);
      const startWidth = parseFloat(currentSizeStr) || header.getSize() || cellElement.clientWidth;

      // Cache last unfixed header info (calculate once)
      const headers = headerGroup.headers;
      const lastUnfixed = findLastUnfixedHeader(headers);

      // Pre-calculate initial total width
      const initialTotalWidth = calculateTableTotalWidth({
        computedStyle: initialComputedStyle,
        hasExpandColumn,
        hasSelectionColumn,
        headers,
        showSideMargin,
      });

      // Get start position
      const startX =
        event.nativeEvent instanceof globalThis.MouseEvent
          ? event.nativeEvent.clientX
          : event.nativeEvent.touches[0].clientX;

      let rafId: number | null = null;
      let prevDeltaX = 0;
      let finalWidth = startWidth; // Track final width for state sync

      // Set resizing state (CSS variable)
      tableRootRef.current.style.setProperty(columnVariableKey.isResizing, '1px');

      // Set resizing state (React state)
      table.setColumnSizingInfo(prev => ({
        ...prev,
        isResizingColumn: header.column.id,
        startOffset: startX,
        startSize: startWidth,
        deltaOffset: 0,
        deltaPercentage: 0,
        columnSizingStart: [[header.column.id, startWidth]],
      }));

      const handlePointerMove = (e: globalThis.MouseEvent | globalThis.TouchEvent) => {
        const currentX = e instanceof globalThis.MouseEvent ? e.clientX : e.touches[0].clientX;
        const deltaX = currentX - startX;
        const newWidth = Math.max(minSize, Math.min(maxSize, startWidth + deltaX));

        // Update final width
        finalWidth = newWidth;

        // Use requestAnimationFrame for smooth updates
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
        }

        rafId = requestAnimationFrame(() => {
          if (!tableRootRef.current) return;

          // Update current column size
          tableRootRef.current.style.setProperty(columnVariableKey.size, `${newWidth}px`);

          // Apply last column auto-sizing logic (use cached values)
          if (lastUnfixed) {
            const { header: lastUnfixedHeader } = lastUnfixed;
            const lastHeaderVariableKey = createColumnVariableKeys(lastUnfixedHeader.column.id);

            // Calculate current total width (optimized - no getComputedStyle)
            const widthDelta = newWidth - startWidth;
            const currentTotalWidth = initialTotalWidth + widthDelta;

            // Apply auto-sizing to last column
            if (hasAutoPadColumn) {
              tableRootRef.current.style.setProperty(
                createBuiltinColumnVariableKeys('autoPadRight').size,
                tableContainerWidth && tableContainerWidth > currentTotalWidth ? 'auto' : '0px',
              );
            } else if (tableContainerWidth && tableContainerWidth > currentTotalWidth) {
              tableRootRef.current.style.setProperty(lastHeaderVariableKey.size, 'auto');
            } else {
              // Only check current value if we need to reset
              const currentLastSize = tableRootRef.current.style.getPropertyValue(
                lastHeaderVariableKey.size,
              );
              if (currentLastSize === 'auto') {
                const size = lastUnfixedHeader.getSize();
                tableRootRef.current.style.setProperty(lastHeaderVariableKey.size, `${size}px`);
              }
            }

            // Auto-scroll when resizing last column to the right
            const isResizingLastColumn = lastUnfixedHeader.id === headerId;
            handleAutoScroll({
              deltaX,
              headerId,
              isResizingLastColumn,
              prevDeltaX,
              tableContainerRef: tableContainerRef.current,
              tableContainerWidth,
              totalWidth: currentTotalWidth,
            });
          }

          // Update previous deltaX for next frame
          prevDeltaX = deltaX;
        });
      };

      const cleanup = () => {
        // Cancel any pending animation frame
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
          rafId = null;
        }

        // Remove resizing state
        if (tableRootRef.current) {
          tableRootRef.current.style.removeProperty(columnVariableKey.isResizing);
        }

        // Clean up event listeners
        document.removeEventListener('mousemove', handlePointerMove as EventListener);
        document.removeEventListener('mouseup', handlePointerUp);
        document.removeEventListener('touchmove', handlePointerMove as EventListener);
        document.removeEventListener('touchend', handlePointerUp);

        // Clear cleanup ref
        cleanupRef.current = null;
      };

      const handlePointerUp = () => {
        // Sync final size to React state for persistence
        // This ensures columnSizing state matches the CSS variable
        table.setColumnSizing(prev => ({ ...prev, [header.column.id]: finalWidth }));

        // Clear resizing state (React state)
        table.setColumnSizingInfo(prev => ({
          ...prev,
          isResizingColumn: false,
          startOffset: null,
          startSize: null,
          deltaOffset: null,
          deltaPercentage: null,
          columnSizingStart: [],
        }));

        cleanup();
      };

      // Store cleanup function for unmount
      cleanupRef.current = cleanup;

      // Add event listeners
      document.addEventListener('mousemove', handlePointerMove as EventListener);
      document.addEventListener('mouseup', handlePointerUp);
      document.addEventListener('touchmove', handlePointerMove as EventListener);
      document.addEventListener('touchend', handlePointerUp);
    },
    [
      table,
      hasAutoPadColumn,
      hasExpandColumn,
      hasSelectionColumn,
      showSideMargin,
      tableContainerWidth,
      tableRootRef,
      tableContainerRef,
    ],
  );

  return { handleColumnResizeEvent };
}
